/**
 * m3 echo Frontend - Insights Tab
 *
 * Admin-only tab for viewing and summarizing Layer 3 insights.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  Checkbox,
  IconButton,
  Tooltip,
  Collapse,
  TextField,
  MenuItem,
  TablePagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  insightsApi,
  adminProjectsApi,
  InsightItem,
  InsightsParams,
} from '../../services/admin';

// ===========================
// Helper Functions
// ===========================

const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';
  const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
  return new Date(utcDateString).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getPositionLabel = (mentioned: boolean, position: number | null): string => {
  if (!mentioned) return 'Nicht erwähnt';
  if (position === null) return 'Erwähnt';
  return `#${position}`;
};

const getPositionColor = (mentioned: boolean, position: number | null): 'success' | 'warning' | 'error' | 'default' => {
  if (!mentioned) return 'error';
  if (position === 1) return 'success';
  if (position && position <= 3) return 'warning';
  return 'default';
};

// ===========================
// Component
// ===========================

export const InsightsTab: React.FC = () => {
  // Data state
  const [insights, setInsights] = useState<InsightItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [projectId, setProjectId] = useState<number | ''>('');
  const [modelName, setModelName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Projects for filter dropdown
  const [projects, setProjects] = useState<{ id: number; name: string }[]>([]);

  // Selection
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Expanded rows
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // CSV export
  const [exporting, setExporting] = useState(false);

  // Summarization
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);

  // Available models (extracted from loaded insights)
  const [availableModels, setAvailableModels] = useState<string[]>([]);

  // ===========================
  // Data Loading
  // ===========================

  const loadInsights = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: InsightsParams = {
        page: page + 1, // API uses 1-based pagination
        page_size: pageSize,
      };
      if (projectId !== '') params.project_id = projectId;
      if (modelName) params.model_name = modelName;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();

      const response = await insightsApi.getInsights(params);
      setInsights(response.data.items);
      setTotal(response.data.total);

      // Extract unique model names for filter
      const models = [...new Set(response.data.items.map(i => i.model_name))];
      setAvailableModels(prev => {
        const combined = new Set([...prev, ...models]);
        return [...combined].sort();
      });
    } catch (err: any) {
      console.error('Error loading insights:', err);
      setError(err.message || 'Fehler beim Laden der Insights');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, projectId, modelName, dateFrom, dateTo]);

  const loadProjects = useCallback(async () => {
    try {
      const response = await adminProjectsApi.getAll();
      const projectList = response.data.map((p) => ({ id: p.id, name: p.name }));
      setProjects(projectList);
    } catch (err) {
      console.error('Error loading projects:', err);
    }
  }, []);

  // ===========================
  // Effects
  // ===========================

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);

  // ===========================
  // Handlers
  // ===========================

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select insights that have a response (not error-only)
      setSelected(new Set(insights.filter(i => !i.error || i.response).map(i => i.id)));
    } else {
      setSelected(new Set());
    }
  };

  const handleSelectOne = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSummarize = async () => {
    if (selected.size < 1) return;

    setSummarizing(true);
    setSummary(null);
    setError(null);

    try {
      const response = await insightsApi.summarize([...selected]);
      setSummary(response.data.summary);
    } catch (err: any) {
      console.error('Error summarizing:', err);
      setError(err.message || 'Fehler bei der Zusammenfassung');
    } finally {
      setSummarizing(false);
    }
  };

  const handleResetFilters = () => {
    setProjectId('');
    setModelName('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const baseParams: Omit<InsightsParams, 'page' | 'page_size'> = {};
      if (projectId !== '') baseParams.project_id = projectId;
      if (modelName) baseParams.model_name = modelName;
      if (dateFrom) baseParams.date_from = new Date(dateFrom).toISOString();
      if (dateTo) baseParams.date_to = new Date(dateTo).toISOString();

      // Paginate through all results (backend limits page_size to 100)
      const items: InsightItem[] = [];
      let currentPage = 1;
      let totalItems = 0;
      do {
        const response = await insightsApi.getInsights({ ...baseParams, page: currentPage, page_size: 100 });
        items.push(...response.data.items);
        totalItems = response.data.total;
        currentPage++;
      } while (items.length < totalItems);

      const escape = (val: string | null | undefined): string => {
        if (val == null) return '';
        const s = String(val).replace(/"/g, '""');
        return `"${s}"`;
      };

      const header = ['Datum', 'Projekt', 'Suchbegriff', 'Modell', 'Position', 'Erwähnt', 'Rückfrage', 'Rückmeldung', 'Fehler'];
      const rows = items.map(i => [
        escape(formatDateTime(i.created_at)),
        escape(i.project_name),
        escape(i.search_term),
        escape(i.model_name),
        i.position !== null ? String(i.position) : '',
        i.mentioned ? 'Ja' : 'Nein',
        escape(i.follow_up_question),
        escape(i.response),
        escape(i.error),
      ].join(';'));

      const bom = '\uFEFF';
      const csv = bom + [header.join(';'), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `insights_export_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Fehler beim CSV-Export');
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
    setSelected(new Set());
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
    setSelected(new Set());
  };

  // ===========================
  // Render
  // ===========================

  const selectableInsights = insights.filter(i => !i.error || i.response);
  const allSelected = selectableInsights.length > 0 && selectableInsights.every(i => selected.has(i.id));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Insights (Layer 3)</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleExportCsv}
            disabled={exporting || loading || insights.length === 0}
          >
            CSV Export
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadInsights}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Filter
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Projekt"
                value={projectId}
                onChange={(e) => { setProjectId(e.target.value === '' ? '' : Number(e.target.value)); setPage(0); }}
              >
                <MenuItem value="">Alle Projekte</MenuItem>
                {projects.map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                label="Modell"
                value={modelName}
                onChange={(e) => { setModelName(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Alle Modelle</MenuItem>
                {availableModels.map(m => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Von"
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                label="Bis"
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button size="small" onClick={handleResetFilters}>
                Zurücksetzen
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Bar */}
      {selected.size > 0 && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2">
              {selected.size} ausgewählt
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={summarizing ? <CircularProgress size={16} /> : <SummarizeIcon />}
              onClick={handleSummarize}
              disabled={summarizing}
            >
              Zusammenfassen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Summary Result */}
      {summary && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Zusammenfassung
            </Typography>
            <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {summary}
              </Typography>
            </Paper>
          </CardContent>
        </Card>
      )}

      {/* Insights Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading && insights.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : insights.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Noch keine Insights vorhanden.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selected.size > 0 && !allSelected}
                          onChange={(_, checked) => handleSelectAll(checked)}
                        />
                      </TableCell>
                      <TableCell>Datum</TableCell>
                      <TableCell>Projekt</TableCell>
                      <TableCell>Suchbegriff</TableCell>
                      <TableCell>Modell</TableCell>
                      <TableCell align="center">Position</TableCell>
                      <TableCell>Rückmeldung</TableCell>
                      <TableCell align="center" width={50}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {insights.map((insight) => {
                      const isError = !!insight.error && !insight.response;
                      return (
                      <React.Fragment key={insight.id}>
                        <TableRow hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selected.has(insight.id)}
                              onChange={() => handleSelectOne(insight.id)}
                              disabled={isError}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatDateTime(insight.created_at)}
                          </TableCell>
                          <TableCell>{insight.project_name || '-'}</TableCell>
                          <TableCell>{insight.search_term}</TableCell>
                          <TableCell>
                            <Chip label={insight.model_name} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={getPositionLabel(insight.mentioned, insight.position)}
                              color={getPositionColor(insight.mentioned, insight.position)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {isError ? (
                              <Chip
                                icon={<ErrorOutlineIcon />}
                                label={insight.error!.substring(0, 60) + (insight.error!.length > 60 ? '...' : '')}
                                color="error"
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <>
                                {(insight.response || '').substring(0, 80)}{(insight.response || '').length > 80 ? '...' : ''}
                              </>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={expanded.has(insight.id) ? 'Zuklappen' : 'Aufklappen'}>
                              <IconButton size="small" onClick={() => handleToggleExpand(insight.id)}>
                                {expanded.has(insight.id) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        {/* Expanded row */}
                        <TableRow>
                          <TableCell colSpan={8} sx={{ py: 0, borderBottom: expanded.has(insight.id) ? undefined : 'none' }}>
                            <Collapse in={expanded.has(insight.id)} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2 }}>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                  Rückfrage:
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                                  {insight.follow_up_question}
                                </Typography>
                                {isError ? (
                                  <>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                      Fehler:
                                    </Typography>
                                    <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {insight.error}
                                    </Alert>
                                  </>
                                ) : (
                                  <>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                      Antwort:
                                    </Typography>
                                    <Paper sx={{ p: 2, backgroundColor: 'grey.50', maxHeight: 300, overflow: 'auto' }}>
                                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {insight.response}
                                      </Typography>
                                    </Paper>
                                  </>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handlePageSizeChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="Pro Seite:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default InsightsTab;
